import { FilterConfig } from './types/filter-config';
import { computed, Signal, signal } from '@angular/core';
import { FieldFilter, Filter } from './types/filter';
import { toQueryFilter } from './helper/to-query-filter.function';
import { withoutField } from './helper/without-field.function';
import { deepCopy, Path, Uuid, uuid } from '@softline/core';

export interface AddFilterOptions {
  operator?: 'AND' | 'OR';
  parent?: Uuid;
}

export class FilterState<T extends object> {

  private _state = signal<Filter<T> | null>(this.config.initialValue)
  state = this._state.asReadonly();

  queryFilter = computed(() => {
    return toQueryFilter(this.state(), this.config)
  })

  /** queryFilter() minus `field`, for a query that binds that field to its own predicate. */
  queryFilterWithout(field: Path<T>) {
    return toQueryFilter(withoutField(this.state(), field), this.config);
  }

  constructor(public config: FilterConfig<T>) { }

  reset() {
    this._state.set(this.config.initialValue);
  }

  clear() {
    this._state.set(null);
  }

  add(filter: Filter<T>, options?: AddFilterOptions): void {
    if(options?.parent)
      this.addSubFilter(filter, options as AddFilterOptions & { parent: Uuid });
    else
      this.addMainFilter(filter, options);
  }

  set(filter: Filter<T>) {
    this._state.set(filter);
  }

  private addMainFilter(filter: Filter<T>, options?: AddFilterOptions): void {
    const currentFilter = this.state();
    let newFilter: Filter<T>;
    if(!currentFilter)
      newFilter = filter;
    else if(currentFilter.type === 'conditions' && currentFilter.operator === 'AND')
      newFilter = {...currentFilter, filters: [...currentFilter.filters, filter]}
    else {
      newFilter = {
        uuid: uuid(),
        type: 'conditions',
        operator: options?.operator ?? 'AND',
        filters: [currentFilter, filter]
      }
    }
    this._state.set(newFilter);
  }

  private addSubFilter(filter: Filter<T>, options: AddFilterOptions & {parent: Uuid}): void {
    let newFilter: Filter<T>;
    const parent = this.find(o => o.uuid === options.parent);
    if(!parent)
      throw new Error(`[QueryFilter:addFilter] Parent Filter with uuid ${parent} not found`);

    if(parent.type === 'conditions' && filter.operator === parent.operator)
      newFilter = { ...parent, filters: [...parent.filters, filter] };
    else
      newFilter = { uuid: uuid(), type: 'conditions', operator: options.operator ?? 'AND', filters: [parent, filter] };
    this.replace(options.parent, newFilter);
  }

  replace(uuid: Uuid, filter: Filter<T>): void {
    const current = deepCopy(this._state());
    if(!current) {
      console.warn(`[FilterService:replaceFilter] Filter not set`);
      return;
    }
    const replaced = this.replaceFilterNode(current, uuid, filter);
    if(!replaced) {
      console.warn(`[FilterService:replaceFilter] Replace Filter with uuid ${uuid} not found`);
      return;
    }
    this._state.set(replaced);
  }

  private replaceFilterNode(
    node: Filter<T>,
    uuid: Uuid,
    replacement: Filter<T>
  ): Filter<T> | undefined {
    if(node.uuid === uuid)
      return replacement;
    if(node.type === 'conditions') {
      let replaced = false;
      const filters = node.filters.map(child => {
        const result = this.replaceFilterNode(child, uuid, replacement);
        if(result) {
          replaced = true;
          return result;
        }
        return child;
      });
      if(replaced)
        return { ...node, filters };
    }
    return undefined;
  }

  remove(uuid: Uuid): void {
    const current = this._state();
    if(!current) {
      console.warn(`[QueryFilter:removeFilter] Filter not set`);
      return;
    }
    const filterWithoutRemoved = this.removeFilterNode(current, uuid);
    if(filterWithoutRemoved === undefined) {
      console.warn(`[QueryFilter:removeFilter] Filter with uuid ${uuid} not found`);
      return;
    }
    this._state.set(filterWithoutRemoved);
  }

  private removeFilterNode(
    node: Filter<T>,
    uuid: Uuid
  ): Filter<T> | null | undefined {
    if(node.uuid === uuid)
      return null;
    if(node.type === 'conditions') {
      let removed = false;
      const filters: Filter<T>[] = [];
      for(const child of node.filters) {
        const result = this.removeFilterNode(child, uuid);
        if(result === undefined)
          filters.push(child);
        else {
          removed = true;
          if(result !== null)
            filters.push(result);
        }
      }
      if(removed) {
        if(filters.length === 0)
          return null;
        if(filters.length === 1)
          return filters[0];
        return { ...node, filters };
      }
    }
    return undefined;
  }

  find(predicate: (filter: Filter<T>) => boolean): Filter<T> | undefined {
    const traverse = [this._state()];
    while (traverse.length > 0) {
      const current = traverse.shift();
      if(!current)
        break;
      if(predicate(current))
        return current;
      if(current.type === 'conditions') {
        traverse.push(...current.filters);
      }
    }
    return undefined;
  }
}
